// Periodic table data: standard atomic weights (IUPAC abridged values; mass number of the
// longest-lived isotope in brackets for elements with no stable isotope). Group, period,
// block and electron configuration are derived from Z, with the known ground-state
// exceptions to the Madelung rule listed explicitly.

export type Category =
  | 'alkali metal'
  | 'alkaline earth metal'
  | 'transition metal'
  | 'post-transition metal'
  | 'metalloid'
  | 'reactive nonmetal'
  | 'halogen'
  | 'noble gas'
  | 'lanthanide'
  | 'actinide';

export type Element = {
  z: number;
  symbol: string;
  name: string;
  mass: string;
  category: Category;
  group: number | null;
  period: number;
  block: 's' | 'p' | 'd' | 'f';
  config: string;
  state: 'solid' | 'liquid' | 'gas' | 'unknown';
  /** Superheavy elements whose chemistry is predicted, not measured. */
  predicted: boolean;
  /** Grid position for the standard 18-column layout, f-block in rows 9-10. */
  row: number;
  col: number;
};

const RAW =
  'H Hydrogen 1.008|He Helium 4.0026|Li Lithium 6.94|Be Beryllium 9.0122|B Boron 10.81|C Carbon 12.011|N Nitrogen 14.007|O Oxygen 15.999|F Fluorine 18.998|Ne Neon 20.180|' +
  'Na Sodium 22.990|Mg Magnesium 24.305|Al Aluminium 26.982|Si Silicon 28.085|P Phosphorus 30.974|S Sulfur 32.06|Cl Chlorine 35.45|Ar Argon 39.95|' +
  'K Potassium 39.098|Ca Calcium 40.078|Sc Scandium 44.956|Ti Titanium 47.867|V Vanadium 50.942|Cr Chromium 51.996|Mn Manganese 54.938|Fe Iron 55.845|Co Cobalt 58.933|Ni Nickel 58.693|Cu Copper 63.546|Zn Zinc 65.38|Ga Gallium 69.723|Ge Germanium 72.630|As Arsenic 74.922|Se Selenium 78.971|Br Bromine 79.904|Kr Krypton 83.798|' +
  'Rb Rubidium 85.468|Sr Strontium 87.62|Y Yttrium 88.906|Zr Zirconium 91.224|Nb Niobium 92.906|Mo Molybdenum 95.95|Tc Technetium [98]|Ru Ruthenium 101.07|Rh Rhodium 102.91|Pd Palladium 106.42|Ag Silver 107.87|Cd Cadmium 112.41|In Indium 114.82|Sn Tin 118.71|Sb Antimony 121.76|Te Tellurium 127.60|I Iodine 126.90|Xe Xenon 131.29|' +
  'Cs Caesium 132.91|Ba Barium 137.33|La Lanthanum 138.91|Ce Cerium 140.12|Pr Praseodymium 140.91|Nd Neodymium 144.24|Pm Promethium [145]|Sm Samarium 150.36|Eu Europium 151.96|Gd Gadolinium 157.25|Tb Terbium 158.93|Dy Dysprosium 162.50|Ho Holmium 164.93|Er Erbium 167.26|Tm Thulium 168.93|Yb Ytterbium 173.05|Lu Lutetium 174.97|' +
  'Hf Hafnium 178.49|Ta Tantalum 180.95|W Tungsten 183.84|Re Rhenium 186.21|Os Osmium 190.23|Ir Iridium 192.22|Pt Platinum 195.08|Au Gold 196.97|Hg Mercury 200.59|Tl Thallium 204.38|Pb Lead 207.2|Bi Bismuth 208.98|Po Polonium [209]|At Astatine [210]|Rn Radon [222]|' +
  'Fr Francium [223]|Ra Radium [226]|Ac Actinium [227]|Th Thorium 232.04|Pa Protactinium 231.04|U Uranium 238.03|Np Neptunium [237]|Pu Plutonium [244]|Am Americium [243]|Cm Curium [247]|Bk Berkelium [247]|Cf Californium [251]|Es Einsteinium [252]|Fm Fermium [257]|Md Mendelevium [258]|No Nobelium [259]|Lr Lawrencium [266]|' +
  'Rf Rutherfordium [267]|Db Dubnium [268]|Sg Seaborgium [269]|Bh Bohrium [270]|Hs Hassium [269]|Mt Meitnerium [278]|Ds Darmstadtium [281]|Rg Roentgenium [282]|Cn Copernicium [285]|Nh Nihonium [286]|Fl Flerovium [289]|Mc Moscovium [290]|Lv Livermorium [293]|Ts Tennessine [294]|Og Oganesson [294]';

const ALKALI = [3, 11, 19, 37, 55, 87];
const ALKALINE = [4, 12, 20, 38, 56, 88];
const POST = [13, 31, 49, 50, 81, 82, 83, 84, 113, 114, 115, 116];
const METALLOID = [5, 14, 32, 33, 51, 52];
const NONMETAL = [1, 6, 7, 8, 15, 16, 34];
const HALOGEN = [9, 17, 35, 53, 85, 117];
const NOBLE = [2, 10, 18, 36, 54, 86, 118];

function categoryOf(z: number): Category {
  if (ALKALI.includes(z)) return 'alkali metal';
  if (ALKALINE.includes(z)) return 'alkaline earth metal';
  if (POST.includes(z)) return 'post-transition metal';
  if (METALLOID.includes(z)) return 'metalloid';
  if (NONMETAL.includes(z)) return 'reactive nonmetal';
  if (HALOGEN.includes(z)) return 'halogen';
  if (NOBLE.includes(z)) return 'noble gas';
  if (z >= 57 && z <= 71) return 'lanthanide';
  if (z >= 89 && z <= 103) return 'actinide';
  return 'transition metal';
}

// Subshells in Madelung (n + l, then n) filling order.
const ORDER = [
  '1s',
  '2s',
  '2p',
  '3s',
  '3p',
  '4s',
  '3d',
  '4p',
  '5s',
  '4d',
  '5p',
  '6s',
  '4f',
  '5d',
  '6p',
  '7s',
  '5f',
  '6d',
  '7p',
];
const CAP: Record<string, number> = { s: 2, p: 6, d: 10, f: 14 };
const NOBLE_CORE: Array<[number, string]> = [
  [86, 'Rn'],
  [54, 'Xe'],
  [36, 'Kr'],
  [18, 'Ar'],
  [10, 'Ne'],
  [2, 'He'],
];

// Measured ground states that differ from the Madelung filling (valence part only).
const EXCEPTIONS: Record<number, string> = {
  24: '3d5 4s1',
  29: '3d10 4s1',
  41: '4d4 5s1',
  42: '4d5 5s1',
  44: '4d7 5s1',
  45: '4d8 5s1',
  46: '4d10',
  47: '4d10 5s1',
  57: '5d1 6s2',
  58: '4f1 5d1 6s2',
  64: '4f7 5d1 6s2',
  78: '4f14 5d9 6s1',
  79: '4f14 5d10 6s1',
  89: '6d1 7s2',
  90: '6d2 7s2',
  91: '5f2 6d1 7s2',
  92: '5f3 6d1 7s2',
  93: '5f4 6d1 7s2',
  96: '5f7 6d1 7s2',
  103: '5f14 7s2 7p1',
};

function configOf(z: number): string {
  const core = NOBLE_CORE.find(([n]) => n < z);
  const prefix = core ? `[${core[1]}] ` : '';
  if (EXCEPTIONS[z]) return prefix + EXCEPTIONS[z];
  let left = z;
  const shells: Array<[string, number]> = [];
  for (const sub of ORDER) {
    if (!left) break;
    const n = Math.min(left, CAP[sub[1]]);
    shells.push([sub, n]);
    left -= n;
  }
  // Drop the electrons already in the noble-gas core, then list by shell (n, then l) as
  // chemistry texts do: [Kr] 4d10 5s2 5p2, not 5s2 4d10 5p2.
  let skip = core ? core[0] : 0;
  const valence = shells
    .map(([sub, n]) => {
      const take = Math.min(skip, n);
      skip -= take;
      return [sub, n - take] as [string, number];
    })
    .filter(([, n]) => n > 0)
    .sort(
      (a, b) =>
        Number(a[0][0]) - Number(b[0][0]) || 'spdf'.indexOf(a[0][1]) - 'spdf'.indexOf(b[0][1]),
    );
  return prefix + valence.map(([sub, n]) => `${sub}${n}`).join(' ');
}

const PERIOD_END = [2, 10, 18, 36, 54, 86, 118];

function place(z: number): {
  group: number | null;
  period: number;
  row: number;
  col: number;
  block: Element['block'];
} {
  const period = PERIOD_END.findIndex((end) => z <= end) + 1;
  const start = period === 1 ? 1 : PERIOD_END[period - 2] + 1;
  const pos = z - start + 1;
  if (period === 1)
    return { group: z === 1 ? 1 : 18, period, row: 1, col: z === 1 ? 1 : 18, block: 's' };
  if (period <= 3) {
    const group = pos <= 2 ? pos : pos + 10;
    return { group, period, row: period, col: group, block: pos <= 2 ? 's' : 'p' };
  }
  if (period <= 5) {
    return {
      group: pos,
      period,
      row: period,
      col: pos,
      block: pos <= 2 ? 's' : pos <= 12 ? 'd' : 'p',
    };
  }
  // Periods 6 and 7: La-Lu / Ac-Lr (positions 3-17) sit in the detached f-block rows.
  if (pos <= 2) return { group: pos, period, row: period, col: pos, block: 's' };
  if (pos <= 17)
    return { group: null, period, row: period + 3, col: pos, block: pos === 17 ? 'd' : 'f' };
  const group = pos - 14;
  return { group, period, row: period, col: group, block: group <= 12 ? 'd' : 'p' };
}

const GAS = [1, 2, 7, 8, 9, 10, 17, 18, 36, 54, 86];
const LIQUID = [35, 80];

export const ELEMENTS: Element[] = RAW.split('|').map((entry, i) => {
  const [symbol, name, mass] = entry.split(' ');
  const z = i + 1;
  const p = place(z);
  return {
    z,
    symbol,
    name,
    mass,
    category: categoryOf(z),
    ...p,
    config: configOf(z),
    state: z >= 100 ? 'unknown' : GAS.includes(z) ? 'gas' : LIQUID.includes(z) ? 'liquid' : 'solid',
    predicted: z >= 109,
  };
});

export const CATEGORY_COLORS: Record<Category, string> = {
  'alkali metal': '#f87171',
  'alkaline earth metal': '#fb923c',
  'transition metal': '#facc15',
  'post-transition metal': '#a3e635',
  metalloid: '#34d399',
  'reactive nonmetal': '#38bdf8',
  halogen: '#22d3ee',
  'noble gas': '#a78bfa',
  lanthanide: '#f472b6',
  actinide: '#e879f9',
};
